/**
 * Is an object seen SIDE-ON in a wall elevation — its front turned across the
 * line of sight, so the drawing shows its DEPTH coming toward the viewer? A
 * peninsula seen from the wall it leaves, or a run on a perpendicular wall
 * reaching into view. The elevation shades those with the same poché it gives
 * a wall seen end-on (`endOnWallBands`), so the casework reads as a mass
 * standing toward you rather than as one more face on the wall.
 *
 * Pure; importable with no environment.
 *
 * The question is asked in the frame the object pass projects with: the
 * wall's normal from `getWallNormal` and the object's own rotation, the same
 * two things `getProjectionView` reads to pick the face it draws. Only the
 * horizontal parts count — an object turned about world up is what reaches
 * toward you, and one lying face-up has no front in the drawing at all. The
 * angle is folded so a back turned to the viewer reads as facing, since the
 * drawing shows a face there too; that also makes the answer independent of
 * which way a wall's normal happens to point (the ±90° wall with no
 * `alignment` is the case that bites elsewhere).
 */
import * as THREE from 'three';
import { CABINET_SURFACE_TYPES } from '../constants/designCenterCategories';
import { APPLIANCE_TYPES } from './objectFinishTakeOffs';
import { getObjectType } from './objectTypeUtils';
import { wallIdsOf } from '@shared-lib/cabinet-runs/cornerTreatment';

/**
 * How far a front must turn from facing the viewer before the elevation shows
 * its depth more than its face. Past 60° the face is foreshortened to under
 * half its width (cos 60° = 0.5) — what reaching toward you looks like; a 45°
 * corner unit, angled rather than reaching, stays unshaded.
 */
export const SIDE_ON_MIN_ANGLE_DEG = 60;

/**
 * What gets the shade: casework and what is built into it. Cabinets of every
 * kind, their end panels and surrounds, the counter on them, and the kitchen
 * and laundry appliances that stand in a run. Not plumbing or lighting
 * fixtures, furniture, or openings — a toilet seen side-on is still a toilet,
 * not a mass of millwork.
 */
const SIDE_ON_SHADED_TYPES = new Set([
  ...CABINET_SURFACE_TYPES,
  'corner-fill-cabinet',
  'tall-cabinet',
  'countertop',
  ...APPLIANCE_TYPES,
]);

const horizontal = (v) => new THREE.Vector3(v.x, 0, v.z);

/**
 * Degrees between the object's front (local +Z) and the wall's normal, in
 * plan, folded into 0–90 so a back turned to the viewer reads 0. `null` when
 * either points straight up or down.
 */
export const angleOffFacingDeg = (wallNormal, objRotation) => {
  const front = horizontal(new THREE.Vector3(0, 0, 1).applyEuler(objRotation));
  const normal = horizontal(wallNormal);
  if (front.lengthSq() < 1e-6 || normal.lengthSq() < 1e-6) return null;
  const cos = Math.min(1, Math.abs(front.normalize().dot(normal.normalize())));
  return THREE.MathUtils.radToDeg(Math.acos(cos));
};

/** Is the object's front turned past `SIDE_ON_MIN_ANGLE_DEG` from facing? */
export const isSeenSideOn = (wallNormal, objRotation) => {
  const angle = angleOffFacingDeg(wallNormal, objRotation);
  return angle !== null && angle > SIDE_ON_MIN_ANGLE_DEG;
};

/** Is this a kind of object the elevation shades when it is seen side-on? */
export const shadesWhenSideOn = (obj) =>
  SIDE_ON_SHADED_TYPES.has(getObjectType(obj));

// ── Corner units ─────────────────────────────────────────────────────────
//
// A corner unit stands on TWO walls, and its `parentWallIds` name both — the
// same list `objectsNearWall` reads to put it on an elevation. Which of them
// the wall being drawn is decides what the drawing is looking at, so that is
// read from the list rather than from the unit's turn: an L unit faces each of
// its legs, and its single "front" (local +Z) only describes the first.
//
// A kitchen APPLIED from the generator names them in `cabinetWallRefs`
// instead — the layout adapter deletes `parentWallIds` — so the list is read
// through `wallIdsOf`, which takes either in the same order. Reading
// `parentWallIds` alone left such a unit with no legs: its other leg went
// unshaded on both its walls (Cabin, 2428 Lore Rd, 2026-10-27).

/** The names an elevation wall answers to: its own id, and for a peninsula or
 * island face, the inline cabinet wall its objects name (`cw_*`). */
const wallNames = (wall) =>
  [wall?.id, wall?.freeSegment?.cabinetWallId].filter(Boolean);

const isSingleSidedCorner = (obj) =>
  obj?.dimensions?.cornerVariant === 'single-sided';

/** An L-shaped corner unit, as `buildCornerCabinet` writes it. */
const isLCornerUnit = (obj) =>
  getObjectType(obj) === 'corner-cabinet' && !isSingleSidedCorner(obj);

/** Does the object name this wall among the two walls of its corner? */
const isCornerUnitOnItsWall = (obj, wall) => {
  const ids = wallIdsOf(obj);
  if (ids.length < 2) return false;
  const names = wallNames(wall);
  return ids.some((id) => names.includes(id));
};

const LEG_RUN = { A: [1, 0, 0], B: [0, 0, 1] };
const LEG_FRONT = { A: [0, 0, 1], B: [1, 0, 0] };
const localAxis = ([x, y, z], objRotation) =>
  new THREE.Vector3(x, y, z).applyEuler(objRotation);

/**
 * Which leg of an L corner unit this wall is, and how that leg lies on the
 * drawing — or null when the wall is not one of its legs.
 *
 * `buildCornerCabinet` seats the unit from the corner: leg A runs along local
 * +X on its first wall (`wallIdsOf`), leg B along local +Z on its second, and
 * the corner box is at the LOW end of both. Leg A's doors face +Z, leg B's +X.
 *
 * @returns {{ leg: 'A'|'B', aligned: boolean, facing: boolean } | null}
 *   `aligned`: the leg runs with screen-right (so its corner end is on the
 *   left). `facing`: its doors face the viewer — false from behind, as from a
 *   peninsula's seating side.
 */
export const cornerLegOnWall = (
  obj,
  wall,
  { wallNormal, wallRight, objRotation }
) => {
  if (!isLCornerUnit(obj)) return null;
  const ids = wallIdsOf(obj);
  if (ids.length < 2) return null;
  const names = wallNames(wall);
  const leg = names.includes(ids[0])
    ? 'A'
    : names.includes(ids[1])
      ? 'B'
      : null;
  if (!leg) return null;
  return {
    leg,
    aligned: localAxis(LEG_RUN[leg], objRotation).dot(wallRight) > 0,
    facing: localAxis(LEG_FRONT[leg], objRotation).dot(wallNormal) > 0,
  };
};

const positive = (v) => (typeof v === 'number' && v > 0 ? v : null);

/**
 * The span of an L unit's drawn box that is its OTHER leg reaching toward the
 * viewer, as fractions of the box from screen-left — or null when the split
 * cannot be read. The split is the unit's own `cornerBox` against the leg's
 * length: what the run's corner plan built, and what `cornerCabinetFace`, the
 * 3D renderer and the plan drawing all read. A unit without one is not given
 * the 24" default; it is drawn as its front alone.
 */
const cornerProtrusion = (obj, { leg, aligned }) => {
  const d = obj.dimensions || {};
  const box = positive(d.cornerBox);
  const len =
    leg === 'A'
      ? (positive(d.alongA) ?? positive(d.x))
      : (positive(d.alongB) ?? positive(d.z));
  if (!box || !len || box >= len) return null;
  const share = box / len;
  return aligned ? { from: 0, to: share } : { from: 1 - share, to: 1 };
};

/**
 * What of an object the elevation shades as seen side-on, as fractions of its
 * drawn box from screen-left — `{ from: 0, to: 1 }` for all of it, a part for
 * an L corner unit on one of its legs, or null for none.
 *
 *   - an L unit on one of its legs: its front on that leg is a front, and the
 *     part of its box that is the OTHER leg — the corner box, at the corner
 *     end — reaches toward you and is shaded. From behind a leg, nothing.
 *   - a blind (single-sided) unit is a straight box with one front: from its
 *     own wall it faces you, and from the corner wall its end is what you see,
 *     all of it reaching toward you — the turn alone says both.
 *   - any other corner unit on one of its walls (an oblique corner fill, a
 *     unit with no box to split): its front, unshaded.
 *   - everything else: all of it when its front is turned past 60°.
 *
 * @param {object} obj
 * @param {object} wall - the wall it is projected onto (a grouped member's
 *   own wall, not the group).
 * @param {{wallNormal: THREE.Vector3, wallRight: THREE.Vector3,
 *   objRotation: THREE.Euler}} frame - the object pass's own basis.
 * @returns {{from: number, to: number} | null}
 */
export const sideOnShadeSpan = (obj, wall, frame) => {
  if (!shadesWhenSideOn(obj)) return null;
  const legView = cornerLegOnWall(obj, wall, frame);
  if (legView) return legView.facing ? cornerProtrusion(obj, legView) : null;
  if (!isSingleSidedCorner(obj) && isCornerUnitOnItsWall(obj, wall)) {
    return null;
  }
  return isSeenSideOn(frame.wallNormal, frame.objRotation)
    ? { from: 0, to: 1 }
    : null;
};
